import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  useTheme,
  Paper,
  Alert,
  Grid,
  Avatar,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  useGetAllShiftsQuery,
  useGetEmployeesQuery,
  useGetAvailabilitiesByEmployeeQuery,
  useGetTimeOffRequestsByEmployeeQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
} from '../../state/api';

const ShiftCalendar = () => {
  const theme = useTheme();

  
  const [currentWeek, setCurrentWeek] = useState(moment().startOf('week'));
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [validationAlert, setValidationAlert] = useState({ show: false, message: '', severity: 'warning' });
  
  const { data: shiftsResponse, isLoading: isLoadingShifts, refetch: refetchShifts } = useGetAllShiftsQuery();
  const { data: employeesResponse, isLoading: isLoadingEmployees } = useGetEmployeesQuery();
  const [createShift] = useCreateShiftMutation();
  const [updateShift] = useUpdateShiftMutation();
  const [deleteShift] = useDeleteShiftMutation();
  
  const shiftsData = shiftsResponse?.data || [];
  const employeesData = employeesResponse?.data || [];
  
  const [shiftData, setShiftData] = useState({
    employeeId: '',
    name: '',
    date: moment().format('YYYY-MM-DD'),
    startTime: '09:00',
    endTime: '17:00',
    breakDuration: 30,
    shiftName: '',
  });

  // Get availability and time-off data for selected employee
  const { data: availabilityData } = useGetAvailabilitiesByEmployeeQuery(
    shiftData.employeeId, 
    { skip: !shiftData.employeeId }
  );
  const { data: timeOffData } = useGetTimeOffRequestsByEmployeeQuery(
    shiftData.employeeId, 
    { skip: !shiftData.employeeId }
  );

  // Generate week days
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(currentWeek.clone().add(i, 'days'));
    }
    return days;
  }, [currentWeek]);

  // Process shifts data for calendar view
  const processedShifts = useMemo(() => {
    if (!shiftsData || !employeesData) return {};
    
    const shiftsByEmployeeAndDate = {};
    
    shiftsData.forEach(shift => {
      // Handle both populated and non-populated employeeId
      let employee = null;
      let employeeId = null;
      
      if (shift.employeeId) {
        if (typeof shift.employeeId === 'object' && shift.employeeId._id) {
          employee = shift.employeeId;
          employeeId = shift.employeeId._id;
        } else {
          employeeId = shift.employeeId;
          employee = employeesData.find(emp => emp._id === shift.employeeId);
        }
      }
      
      if (employee && employeeId) {
        const shiftDate = moment(shift.date).format('YYYY-MM-DD');
        
        if (!shiftsByEmployeeAndDate[employeeId]) {
          shiftsByEmployeeAndDate[employeeId] = {};
        }
        
        if (!shiftsByEmployeeAndDate[employeeId][shiftDate]) {
          shiftsByEmployeeAndDate[employeeId][shiftDate] = [];
        }
        
        shiftsByEmployeeAndDate[employeeId][shiftDate].push({
          ...shift,
          employee: employee,
          startTime: moment(shift.startTime),
          endTime: moment(shift.endTime),
        });
      }
    });
    
    return shiftsByEmployeeAndDate;
  }, [shiftsResponse, employeesResponse]);

  const handlePreviousWeek = () => {
    setCurrentWeek(prev => prev.clone().subtract(1, 'week'));
  };

  const handleNextWeek = () => {
    setCurrentWeek(prev => prev.clone().add(1, 'week'));
  };

  const handleToday = () => {
    setCurrentWeek(moment().startOf('week'));
  };

  const handleCellClick = (employee, date) => {
    setSelectedEmployee(employee);
    setSelectedDate(date);
    setShiftData({
      employeeId: employee._id,
      name: `${employee.names.firstName} ${employee.names.lastName}'s shift`,
      date: date.format('YYYY-MM-DD'),
      startTime: '09:00',
      endTime: '17:00',
      breakDuration: 30,
      shiftName: '',
    });
    setOpenAddDialog(true);
  };

  const handleShiftClick = (shift, event) => {
    event.stopPropagation();
    setSelectedShift(shift);
    setShiftData({
      ...shift,
      employeeId: typeof shift.employeeId === 'object' ? shift.employeeId._id : shift.employeeId,
      date: moment(shift.date).format('YYYY-MM-DD'),
      startTime: moment(shift.startTime).format('HH:mm'),
      endTime: moment(shift.endTime).format('HH:mm'),
      shiftName: shift.name,
    });
    setOpenEditDialog(true);
  };

  const performValidationChecks = async (shiftData, isEdit = false) => {
    const shiftStart = moment(`${shiftData.date} ${shiftData.startTime}`);
    const shiftEnd = moment(`${shiftData.date} ${shiftData.endTime}`);
    const shiftDay = shiftStart.format('dddd');

    // 1. Check for Approved Time Off
    if (timeOffData && timeOffData.length > 0) {
      const conflictingTimeOff = timeOffData.find(request => 
        request.status === 'Approved' &&
        (
          (shiftStart.isBetween(moment(request.startDate), moment(request.endDate), null, '[]')) ||
          (shiftEnd.isBetween(moment(request.startDate), moment(request.endDate), null, '[]')) ||
          (moment(request.startDate).isBetween(shiftStart, shiftEnd, null, '[]'))
        )
      );

      if (conflictingTimeOff) {
        return {
          canProceed: false,
          message: `Employee has approved ${conflictingTimeOff.requestType} from ${moment(conflictingTimeOff.startDate).format('DD/MM/YYYY')} to ${moment(conflictingTimeOff.endDate).format('DD/MM/YYYY')}`,
          severity: 'error'
        };
      }
    }

    // 2. Check Weekly Availability
    if (availabilityData && availabilityData.length > 0) {
      const availability = availabilityData[0];
      const dayPattern = availability.weeklyPattern?.find(pattern => pattern.dayOfWeek === shiftDay);
      
      if (!dayPattern || !dayPattern.isAvailable) {
        return {
          canProceed: false,
          message: `Employee is not available on ${shiftDay}. Please check their availability schedule.`,
          severity: 'warning'
        };
      }
    }

    // 3. Check for Conflicting Shifts
    if (shiftsData && shiftsData.length > 0) {
      const conflictingShift = shiftsData.find(shift => 
        (typeof shift.employeeId === 'object' ? shift.employeeId._id : shift.employeeId) === shiftData.employeeId &&
        (!isEdit || shift._id !== shiftData._id) &&
        (
          (shiftStart.isBetween(moment(shift.startTime), moment(shift.endTime), null, '()')) ||
          (shiftEnd.isBetween(moment(shift.startTime), moment(shift.endTime), null, '()')) ||
          (moment(shift.startTime).isBetween(shiftStart, shiftEnd, null, '()'))
        )
      );

      if (conflictingShift) {
        return {
          canProceed: false,
          message: `Employee already has a conflicting shift on ${moment(conflictingShift.date).format('DD/MM/YYYY')}`,
          severity: 'error'
        };
      }
    }

    return { canProceed: true };
  };

  const handleAddSubmit = async () => {
    if (!shiftData.employeeId || !shiftData.date || !shiftData.startTime || !shiftData.endTime) {
      setValidationAlert({ show: true, message: "Please fill in all required fields.", severity: 'error' });
      return;
    }
    
    try {
      const validationResult = await performValidationChecks(shiftData);
      if (!validationResult.canProceed) {
        setValidationAlert({ show: true, message: validationResult.message, severity: validationResult.severity });
        return;
      }

      const shiftStart = moment(`${shiftData.date} ${shiftData.startTime}`);
      const shiftEnd = moment(`${shiftData.date} ${shiftData.endTime}`);
      
      const shiftToCreate = {
        ...shiftData,
        startTime: shiftStart.toISOString(),
        endTime: shiftEnd.toISOString(),
        name: shiftData.shiftName,
      };

      await createShift(shiftToCreate).unwrap();
      setOpenAddDialog(false);
      setValidationAlert({ show: false, message: '', severity: 'warning' });
      refetchShifts();
    } catch (error) {

      setValidationAlert({ show: true, message: `Failed to create shift: ${error.data?.message || error.message}`, severity: 'error' });
    }
  };

  const handleEditSubmit = async () => {
    try {
      const validationResult = await performValidationChecks(shiftData, true);
      if (!validationResult.canProceed) {
        setValidationAlert({ show: true, message: validationResult.message, severity: validationResult.severity });
        return;
      }

      const shiftStart = moment(`${shiftData.date} ${shiftData.startTime}`);
      const shiftEnd = moment(`${shiftData.date} ${shiftData.endTime}`);
      
      const updateData = {
        ...shiftData,
        startTime: shiftStart.toISOString(),
        endTime: shiftEnd.toISOString(),
        name: shiftData.shiftName,
      };

      await updateShift({ id: selectedShift._id, ...updateData });
      setOpenEditDialog(false);
      setValidationAlert({ show: false, message: '', severity: 'warning' });
      refetchShifts();
    } catch (error) {

      setValidationAlert({ show: true, message: `Failed to update shift: ${error.data?.message || error.message}`, severity: 'error' });
    }
  };

  const handleDeleteShift = async () => {
    try {
      await deleteShift(selectedShift._id);
      setOpenEditDialog(false);
      refetchShifts();
    } catch (error) {

    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShiftData(prev => ({ ...prev, [name]: value }));
  };

  const getShiftColor = (shift) => {
    const colors = [
      theme.palette.primary[300],
      theme.palette.secondary[300],
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.error.main,
    ];
    
    const employeeId = typeof shift.employeeId === 'object' ? shift.employeeId._id : shift.employeeId;
    const index = employeeId ? employeeId.charCodeAt(employeeId.length - 1) % colors.length : 0;
    return colors[index];
  };

  if (isLoadingShifts || isLoadingEmployees) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading calendar...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Shift Calendar</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Button
              onClick={handleToday}
              sx={{
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.background.alt,
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': {
                      backgroundColor: theme.palette.secondary.dark
                  }
              }}
          >
              Today
          </Button>
          <IconButton onClick={handlePreviousWeek}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: '200px', textAlign: 'center', color: theme.palette.secondary[200] }}>
            {currentWeek.format('MMM DD')} - {currentWeek.clone().add(6, 'days').format('MMM DD, YYYY')}
          </Typography>
          <IconButton onClick={handleNextWeek}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Calendar Grid */}
      <Paper sx={{ overflow: 'hidden' }}>
        {/* Header Row */}
        <Grid container sx={{ borderBottom: `1px solid ${theme.palette.secondary[300]}` }}>
          <Grid item xs={2} sx={{ p: 2, borderRight: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.primary.light }}>
            <Typography variant="subtitle1" fontWeight="bold">Employee</Typography>
          </Grid>
          {weekDays.map((day, index) => (
            <Grid key={index} item xs={10/7} sx={{ p: 2, borderRight: index < 6 ? `1px solid ${theme.palette.divider}` : 'none', backgroundColor: theme.palette.primary.light }}>
              <Typography variant="subtitle2" fontWeight="bold" textAlign="center">
                {day.format('ddd')}
              </Typography>
              <Typography variant="caption" textAlign="center" display="block">
                {day.format('MMM DD')}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Employee Rows */}
        {employeesData.map((employee, employeeIndex) => (
          <Grid key={employee._id} container sx={{ borderBottom: `1px solid ${theme.palette.divider}`, minHeight: '80px', backgroundColor: theme.palette.background.alt }}>
            {/* Employee Name Column */}
            <Grid item xs={2} sx={{ p: 2, borderRight: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar
                  sx={{
                    width: 37,
                    height: 37,
                    fontSize: '0.875rem',
                    color: theme.palette.secondary[200],
                    backgroundColor: theme.palette.primary.light,
                    }}>
                  {employee.names.firstName[0]}{employee.names.lastName[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="medium">
                    {employee.names.firstName} {employee.names.lastName}
                  </Typography>
                  <Typography variant="caption" color={theme.palette.secondary[300]}>
                    {employee.department}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Day Columns */}
            {weekDays.map((day, dayIndex) => {
              const dayKey = day.format('YYYY-MM-DD');
              const dayShifts = processedShifts[employee._id]?.[dayKey] || [];
              
              return (
                <Grid 
                  key={dayIndex} 
                  item 
                  xs={10/7} 
                  sx={{ 
                    p: 1, 
                    borderRight: dayIndex < 6 ? `1px solid ${theme.palette.divider}` : 'none',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5
                  }}
                  onClick={() => handleCellClick(employee, day)}
                >
                  {dayShifts.map((shift, shiftIndex) => (
                    <Box key={shift._id || `shift-${shiftIndex}`}>
                      <Paper
                        //label={`${shift.startTime.format('HH:mm')}-${shift.endTime.format('HH:mm')}`}
                        elevation={7} // Give it a slight shadow to lift it
                        sx={{
                          p: 1,
                          borderRadius: '1px', // Make it rounded like a chip
                          opacity: 0.7,
                          backgroundColor: getShiftColor(shift),
                          color: theme.palette.secondary[600],
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          textAlign: 'center', // Center the text
                          '&:hover': {
                            backgroundColor: theme.palette.secondary[100],
                            boxShadow: 4, // Add more shadow on hover
                          }
                        }}
                        onClick={(e) => handleShiftClick(shift, e)}
                      >
                        {/* Now we can have as many lines as we want inside */}
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1.2 }}>
                          {`${shift.startTime.format('HH:mm')}-${shift.endTime.format('HH:mm')}`}
                        </Typography>
                        <Typography sx={{ fontSize: '0.5rem', lineHeight: 1.7 }}>
                          {shift.name}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Paper>

      {/* Add Shift Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Schedule New Shift</DialogTitle>
        <DialogContent>
          {validationAlert.show && (
            <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
              {validationAlert.message}
            </Alert>
          )}
          <Typography sx={{ mb: 2 }}>
            <strong>Employee:</strong> {selectedEmployee?.names.firstName} {selectedEmployee?.names.lastName}
          </Typography>
          <Typography sx={{ mb: 2 }}>
            <strong>Date:</strong> {selectedDate?.format('dddd, MMMM DD, YYYY')}
          </Typography>
          <TextField
            margin="dense"
            label="Start Time*"
            name="startTime"
            type="time"
            value={shiftData.startTime}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="End Time*"
            name="endTime"
            type="time"
            value={shiftData.endTime}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Break Duration (minutes)"
            name="breakDuration"
            type="number"
            value={shiftData.breakDuration}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
          />
          <TextField
            margin="dense"
            label="Shift Name"
            name="name"
            value={shiftData.name}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenAddDialog(false)}
            variant="contained"
            color="secondary"
            sx={{
              backgroundColor: theme.palette.error.main,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSubmit}
            variant="contained"
            color="secondary"
            sx={{
              backgroundColor: theme.palette.secondary.main,
            }}
          >
            Schedule Shift
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Shift</DialogTitle>
        <DialogContent>
          {validationAlert.show && (
            <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
              {validationAlert.message}
            </Alert>
          )}
          <TextField
            margin="dense"
            label="Date"
            name="date"
            type="date"
            value={shiftData.date}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Start Time"
            name="startTime"
            type="time"
            value={shiftData.startTime}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="End Time"
            name="endTime"
            type="time"
            value={shiftData.endTime}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Break Duration (minutes)"
            name="breakDuration"
            type="number"
            value={shiftData.breakDuration}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
          />
          <TextField
            margin="dense"
            label="Shift Name"
            name="name"
            value={shiftData.name}
            onChange={handleInputChange}
            fullWidth
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteShift}
            variant="contained"
            color="secondary"
            sx={{
              backgroundColor: theme.palette.error.main,
            }}
          >
            Delete
          </Button>
          <Button
            onClick={() => setOpenEditDialog(false)}
            variant="contained"
            color="secondary"
            sx={{
              backgroundColor: theme.palette.primary.main,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            color="secondary"
            sx={{
              backgroundColor: theme.palette.secondary.main,
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftCalendar;
